import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { switchMap, map, combineLatest, of, catchError, shareReplay, forkJoin } from 'rxjs';
import { WebsiteGroupRecord } from '../../shared/models/website-group.models';
import { PlansService } from '../../core/services/plans.service';
import { ServersService } from '../../core/services/servers.service';
import { WebsiteGroupsService } from '../../core/services/website-groups.service';
import { WebsitesService } from '../../core/services/websites.service';
import { SearchFieldComponent } from '../../shared/components/search-field/search-field.component';
import { PageEvent } from '@angular/material/paginator';
import { MatCardModule } from '@angular/material/card';
import { TableComponent } from '../../shared/components/table/table.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AsyncPipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { UsageService } from '../../core/services/usage.service';
import { BytesToSizePipe } from '../../shared/pipes/bytes-to-size-pipe';
import { MatRipple } from '@angular/material/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-usage',
  imports: [
    SearchFieldComponent,
    MatCardModule,
    TableComponent,
    FontAwesomeModule,
    MatTableModule,
    MatTooltipModule,
    MatRipple,
    RouterLink,
    AsyncPipe,
  ],
  providers: [BytesToSizePipe],
  templateUrl: './usage.component.html',
  styleUrl: './usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsageComponent {
  private websitesService = inject(WebsitesService);

  private serversService = inject(ServersService);

  private websiteGroupsService = inject(WebsiteGroupsService);

  private plansService = inject(PlansService);

  private usageService = inject(UsageService);

  private sizePipe = inject(BytesToSizePipe);

  plans$ = this.plansService.getAll().pipe(shareReplay(1));

  servers$ = this.serversService.getAllServers().pipe(
    catchError(() => of([])),
    shareReplay(1),
  );

  groupsFilters = signal<{ page: number; owner?: string; itemsPerPage: number; domain?: string }>({
    page: 1,
    itemsPerPage: 10,
  });

  groupsFilters$ = toObservable(this.groupsFilters);

  totalGroups = signal(0);

  groups$ = this.groupsFilters$.pipe(
    switchMap((filters) => this.websiteGroupsService.searchGroups(filters)),
    map((websiteGroups) => ({
      websiteGroups,
      websiteIds: websiteGroups.groups.map((group) => group.mainWebsite),
    })),
    switchMap(({ websiteGroups, websiteIds }) =>
      combineLatest({
        websites: this.websitesService.getWebsitesByIds(websiteIds).pipe(map((w) => w.Websites)),
        websiteGroups: of(websiteGroups),
        plans: this.plans$,
        servers: this.servers$,
      }),
    ),
    switchMap(({ websites, websiteGroups, plans, servers }) => {
      this.totalGroups.set(websiteGroups.count);

      return forkJoin(
        websiteGroups.groups.map((group) =>
          this.usageService.getDiskUsageByGroup(group.id).pipe(
            catchError(() => of(undefined)),
            map((usage) => {
              const groupWebsite = websites.find((website) => website.id === group.mainWebsite);
              const server = servers.find((s) => s._id === groupWebsite?.originServer);
              const plan = plans[group.plan!];

              return {
                id: group.id,
                plan,
                website: groupWebsite!,
                owner: group.owner,
                websiteCount: group.websiteCount,
                server,
                usage,
              };
            }),
          ),
        ),
      );
    }),
    shareReplay(1),
  );

  groupsColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: WebsiteGroupRecord) => `${element.id}`,
    },
    {
      columnDef: 'plan',
      header: 'Plan',
      cell: (element: WebsiteGroupRecord) => `${element.plan?.name || '-'}`,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: WebsiteGroupRecord) => `${element.website?.domain || '-'}`,
    },
    {
      columnDef: 'server',
      header: 'Server',
      cell: (element: WebsiteGroupRecord) => `${element.server?.hostname || '-'}`,
    },
    {
      columnDef: 'quota',
      header: 'Used Disk Quota',
      cell: (element: WebsiteGroupRecord) =>
        element.usage?.quotaSnapshot
          ? `${this.sizePipe.transform(element.usage.quotaSnapshot.blockUsed)} / ${this.sizePipe.transform(element.usage.quotaSnapshot.blockHard)}`
          : 'N / A',
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  handlePageEvent(event: PageEvent) {
    this.groupsFilters.update((f) => ({ ...f, page: event.pageIndex + 1 }));
  }

  onSearch(text: string) {
    this.groupsFilters.update((f) => ({ ...f, domain: text }));
  }
}
